import { SxProps } from "@mui/material";
import { PropsWithChildren, ReactNode } from "react";
import { OptionalClassName } from "../../utils/types";

export type DataTableCellValue =
  | string
  | number
  | boolean
  | null
  | ReactNode
  | Record<string, any>;

type FormatDataTableCell = (
  value: DataTableCellValue,
  rowData?: DataTableRowData
) => ReactNode | DataTableCellValue;

export type FormatCellObject = Record<string, FormatDataTableCell>;

export type DataTableLabels = Record<string, string>;

export type DataTableRowData = Record<string, DataTableCellValue>;

export type DataTableProps = OptionalClassName<{
  data: DataTableRowData[];
  labels?: DataTableLabels;
  formatCell?: FormatCellObject;
  onEdit?: () => void;
  onDelete?: () => void;
  getKey: (row: DataTableRowData) => string; // Function to get unique key for each row
  sx?: SxProps;
  disabledRows?: (string | number)[];
  bottomWidget?: React.ReactNode; // Optional bottom widget to be displayed below the table
  selectedRows?: (string | number)[];
  onSelect?: ((id: string | string[]) => void) | false;
  onDeselect?: ((id: string | string[]) => void) | false;
  storageKey?: string;
  perPageOptions?: number[];
  perPage?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  total?: number;
  hiddenColumns?: string[];
}>;

export type DataTableRowProps = {
  formatCell?: FormatCellObject;
  data: any;
  onSelect?: () => void;
  selected: boolean;
  disabled?: boolean;
  storageKey?: string;
  hiddenColumns: string[];
};

export type DataTableCellProps = PropsWithChildren<{
  shrink?: boolean;
  sx?: SxProps;
}>;

export type UseDataTableValuesArgs = Pick<
  DataTableProps,
  "getKey" | "data" | "disabledRows" | "selectedRows"
>;

export enum FilterType {
  Contains = "contains",
  DoesNotContain = "does not contain",
  Is = "is",
  IsNot = "is not",
  startsWith = "starts with",
  endsWith = "ends with",
  isNotSet = "is not set",
  isSet = "is set",
}

export enum TableCellInputType {
  Text = "text",
  Select = "select",
  Date = "date",
  DateTime = "datetime",
  MultiLine = "multiline",
  FileUpload = "fileupload",
  UserInput = "userinput",
  Boolean = "boolean",
  Form = "form",
  SystemProvided = "systemprovided",
}

export const allFilterMethods = Object.values(FilterType ?? {});

export const filterMethodsByInputType: Partial<
  Record<TableCellInputType, FilterType[]>
> = {
  [TableCellInputType.Boolean]: [FilterType?.Is, FilterType?.IsNot],
  [TableCellInputType.Select]: [
    FilterType?.Is,
    FilterType?.IsNot,
    FilterType?.isSet,
    FilterType?.isNotSet,
  ],
};

